import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { PlanDetails, SubscriptionDetails } from '@nvgc/shared';

interface BillingManagementProps {
  onClose?: () => void;
}

export const BillingManagement: React.FC<BillingManagementProps> = ({ onClose }) => {
  const [plans, setPlans] = useState<PlanDetails[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    plan: PlanDetails | null;
  }>({ open: false, plan: null });

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [plansResponse, subscriptionResponse] = await Promise.all([
        fetch('/api/billing/plans'),
        fetch('/api/billing/subscription', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        })
      ]);

      if (!plansResponse.ok) {
        throw new Error('Failed to load plans');
      }

      if (!subscriptionResponse.ok) {
        throw new Error('Failed to load subscription');
      }

      const plansData = await plansResponse.json();
      const subscriptionData = await subscriptionResponse.json();

      setPlans(plansData.data);
      setSubscription(subscriptionData.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: PlanDetails) => {
    if (plan.id === 'trial') {
      return; // Can't "upgrade" to trial
    }

    setConfirmDialog({ open: true, plan });
  };

  const confirmUpgrade = async () => {
    const plan = confirmDialog.plan;
    if (!plan) return;

    try {
      setUpgrading(true);
      setError(null);

      // Create checkout session
      const response = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          priceId: plan.stripePriceId,
          successUrl: `${window.location.origin}/billing?success=true`,
          cancelUrl: `${window.location.origin}/billing?canceled=true`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
      if (stripe) {
        await stripe.redirectToCheckout({ sessionId: data.data.sessionId });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start upgrade process');
    } finally {
      setUpgrading(false);
      setConfirmDialog({ open: false, plan: null });
    }
  };

  const handleManageSubscription = async () => {
    try {
      setUpgrading(true);
      setError(null);

      const response = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          returnUrl: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Customer Portal
      window.location.href = data.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setUpgrading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  };

  const isCurrentPlan = (planId: string) => {
    return subscription?.planType === planId;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadBillingData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Billing & Subscription
        </Typography>
        {onClose && (
          <Button onClick={onClose}>
            Close
          </Button>
        )}
      </Box>

      {subscription && (
        <Alert 
          severity={getStatusColor(subscription.status) as any}
          sx={{ mb: 3 }}
        >
          <Typography variant="body1">
            Current Plan: <strong>{subscription.planType.toUpperCase()}</strong>
            {' '}
            <Chip 
              label={subscription.status}
              size="small"
              color={getStatusColor(subscription.status) as any}
              sx={{ ml: 1 }}
            />
          </Typography>
          {subscription.status === 'active' && subscription.stripeCustomerId && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleManageSubscription}
              disabled={upgrading}
              sx={{ mt: 1 }}
            >
              Manage Subscription
            </Button>
          )}
        </Alert>
      )}

      <Typography variant="h5" gutterBottom>
        Available Plans
      </Typography>

      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} md={6} lg={3} key={plan.id}>
            <Card 
              variant={isCurrentPlan(plan.id) ? "outlined" : "elevation"}
              sx={{ 
                height: '100%',
                position: 'relative',
                border: isCurrentPlan(plan.id) ? 2 : undefined,
                borderColor: isCurrentPlan(plan.id) ? 'primary.main' : undefined
              }}
            >
              {plan.id === 'pro' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: 16,
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <StarIcon fontSize="small" />
                  <Typography variant="caption">Popular</Typography>
                </Box>
              )}

              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h5" gutterBottom>
                  {plan.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {plan.description}
                </Typography>
                
                <Box my={2}>
                  <Typography variant="h3" color="primary">
                    {plan.price === 0 ? 'Free' : formatPrice(plan.price)}
                  </Typography>
                  {plan.price > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      per {plan.interval}
                    </Typography>
                  )}
                </Box>

                <List dense sx={{ flexGrow: 1 }}>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <CheckIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                      <ListItemText 
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2 }} />

                <Button
                  variant={isCurrentPlan(plan.id) ? "outlined" : "contained"}
                  fullWidth
                  disabled={isCurrentPlan(plan.id) || upgrading || plan.id === 'trial'}
                  onClick={() => handleUpgrade(plan)}
                >
                  {isCurrentPlan(plan.id) 
                    ? 'Current Plan' 
                    : plan.id === 'trial' 
                      ? 'Trial Plan' 
                      : 'Upgrade'
                  }
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, plan: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Plan Upgrade
        </DialogTitle>
        <DialogContent>
          {confirmDialog.plan && (
            <Box>
              <Typography variant="body1" gutterBottom>
                You're about to upgrade to the <strong>{confirmDialog.plan.name}</strong> plan.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Price: {formatPrice(confirmDialog.plan.price)} per {confirmDialog.plan.interval}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You'll be redirected to Stripe to complete the payment.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, plan: null })}
            disabled={upgrading}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmUpgrade}
            variant="contained"
            disabled={upgrading}
          >
            {upgrading ? <CircularProgress size={20} /> : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};